import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../../../core/design-system/components/Icon';
import { useAssistantChat } from '../../../posts/hooks/useAssistantChat';
import { useAuth } from '../../../../core/providers/AuthProvider';
import LlmModal from '../../components/LlmModal';
import LocalEventsModal from '../../components/LocalEventsModal';
import { HomePage } from '../../../home';
import { ActivityDetailsPage } from '../../../activities';
import FeedPreferencesPage from '../../../settings/pages/FeedPreferencesPage';
import CreateActivityAssistant from '../../../posts/components/CreateActivityAssistant';
import './styles.css';

const LlmDashboard = () => {
  const { t } = useTranslation('common');
  const { user, isAuthenticated } = useAuth();
  const [query, setQuery] = useState('');
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const containerRef = useRef(null);
  
  // Navigation stack pour le modal (permet le retour en arrière)
  // Format: [{ type: 'feed' }, { type: 'activity', id: 'xxx' }]
  const [modalStack, setModalStack] = useState([]);
  
  // Helpers pour la navigation modale
  const pushModal = useCallback((screen) => {
    setModalStack(prev => [...prev, screen]);
  }, []);
  
  const popModal = useCallback(() => {
    setModalStack(prev => {
      if (prev.length <= 1) {
        // Si on est au premier écran, fermer le modal
        return [];
      }
      // Sinon, retirer le dernier écran
      return prev.slice(0, -1);
    });
  }, []);
  
  const closeModal = useCallback(() => {
    setModalStack([]);
  }, []);
  
  // État courant du modal
  const currentModal = modalStack.length > 0 ? modalStack[modalStack.length - 1] : null;
  const isModalOpen = modalStack.length > 0;

  // Fonction pour parser le contenu et rendre les liens d'activité cliquables
  const renderMessageContent = useCallback((content) => {
    if (!content) return null;
    
    // Regex pour détecter "Détails : URL" ou juste l'URL d'activité
    // Capture optionnelle du préfixe "Détails : " pour le supprimer
    const activityUrlRegex = /(?:Détails\s*:\s*)?(https?:\/\/[^\s]+\/dashboard\/activity\/([a-zA-Z0-9]+))/g;
    
    // Split le contenu par les URLs
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = activityUrlRegex.exec(content)) !== null) {
      // Ajouter le texte avant l'URL (sans "Détails : ")
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: content.slice(lastIndex, match.index)
        });
      }
      
      // Ajouter l'URL comme lien cliquable
      parts.push({
        type: 'activity-link',
        url: match[1],
        activityId: match[2]
      });
      
      lastIndex = match.index + match[0].length;
    }
    
    // Ajouter le texte restant après la dernière URL
    if (lastIndex < content.length) {
      parts.push({
        type: 'text',
        content: content.slice(lastIndex)
      });
    }
    
    // Si aucune URL trouvée, retourner le contenu tel quel
    if (parts.length === 0) {
      return content;
    }
    
    // Rendre les parties
    return parts.map((part, index) => {
      if (part.type === 'text') {
        // Ignorer les parties vides
        if (!part.content) return null;
        return <span key={index}>{part.content}</span>;
      }
      
      if (part.type === 'activity-link') {
        return (
          <button
            key={index}
            type="button"
            className="llm-activity-link"
            onClick={(e) => {
              e.preventDefault();
              if (process.env.NODE_ENV === 'development') {
                console.log('[LlmDashboard] Opening activity modal for:', part.activityId);
              }
              pushModal({ type: 'activity', id: part.activityId });
            }}
          >
            {t('activity.viewDetails', 'Voir la fiche')} →
          </button>
        );
      }
      
      return null;
    });
  }, [pushModal, t]);
  
  // Utiliser le hook useAssistantChat pour la communication avec l'agent
  const { 
    messages, 
    isLoading, 
    sendMessage
  } = useAssistantChat();

  // Déterminer l'état utilisateur
  const userState = useMemo(() => {
    if (!isAuthenticated || !user) return 'anonymous';
    if (!user.onboardingCompleted) return 'connected_no_prefs';
    return 'connected_onboarded';
  }, [isAuthenticated, user]);

  // CTAs adaptés selon l'état utilisateur avec icônes
  const suggestedQueries = useMemo(() => {
    const ctas = {
      // Non connecté / non onboardé
      anonymous: [
        { icon: 'compass', text: t('llm.suggestions.anonymous.nearby', 'Activités autour de moi') },
        { icon: 'clock', text: t('llm.suggestions.anonymous.today', 'Que faire aujourd\'hui ?') },
        { icon: 'sunset', text: t('llm.suggestions.anonymous.weekend', 'Idées pour ce week-end') },
        { icon: 'users', text: t('llm.suggestions.anonymous.group', 'Sorties en groupe') },
        { icon: 'sparkles', text: t('llm.suggestions.anonymous.create', 'Créer une activité') },
      ],
      // Connecté sans préférences
      connected_no_prefs: [
        { icon: 'map-pin', text: t('llm.suggestions.connected.nearby', 'Activités près de chez moi') },
        { icon: 'clock', text: t('llm.suggestions.connected.today', 'Aujourd\'hui / Demain') },
        { icon: 'sunset', text: t('llm.suggestions.connected.weekend', 'Idées pour le week-end') },
        { icon: 'running', text: t('llm.suggestions.connected.sport', 'Activité sportive') },
        { icon: 'palette', text: t('llm.suggestions.connected.cultural', 'Culture / sorties') },
        { icon: 'sparkles', text: t('llm.suggestions.connected.create', 'Créer une activité') },
      ],
      // Connecté + onboardé
      connected_onboarded: [
        { icon: 'star', text: t('llm.suggestions.onboarded.recommendations', 'Recommandations pour toi') },
        { icon: 'compass', text: t('llm.suggestions.onboarded.nearby', 'Activités autour de toi') },
        { icon: 'zap', text: t('llm.suggestions.onboarded.tonight', 'Ce soir / Ce week-end') },
        { icon: 'sparkles', text: t('llm.suggestions.onboarded.create', 'Créer une activité') },
      ],
    };
    return ctas[userState] || ctas.anonymous;
  }, [userState, t]);

  // On est en mode "welcome" si il n'y a pas de messages utilisateur
  const hasUserMessages = messages.some(m => m.role === 'user' || m.type === 'user');
  
  // Messages à afficher (tous sauf le welcome initial quand on est en mode conversation)
  const displayMessages = hasUserMessages ? messages.filter(m => m.id !== 'welcome') : [];

  // Mode welcome = input centré avec le contenu
  const isWelcomeMode = displayMessages.length === 0;

  // Gestion du Visual Viewport pour le clavier mobile
  useEffect(() => {
    const updateViewportHeight = () => {
      const vh = window.visualViewport?.height || window.innerHeight;
      document.documentElement.style.setProperty('--llm-viewport-height', `${vh}px`);
    };

    updateViewportHeight();

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateViewportHeight);
      window.visualViewport.addEventListener('scroll', updateViewportHeight);
    } else {
      window.addEventListener('resize', updateViewportHeight);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateViewportHeight);
        window.visualViewport.removeEventListener('scroll', updateViewportHeight);
      } else {
        window.removeEventListener('resize', updateViewportHeight);
      }
    };
  }, []);

  // Auto-resize du textarea (pattern ChatGPT/Perplexity)
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, 200);
      textarea.style.height = `${newHeight}px`;
    }
  }, []);

  // Ajuster la hauteur quand le texte change
  useEffect(() => {
    adjustTextareaHeight();
  }, [query, adjustTextareaHeight]);

  // Auto-scroll vers le bas quand de nouveaux messages arrivent
  useEffect(() => {
    if (hasUserMessages) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, hasUserMessages]);

  // Soumettre un message
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!query.trim() || isLoading) return;
    
    const messageToSend = query.trim();
    setQuery('');
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    
    // Garder le focus après envoi
    textareaRef.current?.focus();
    
    await sendMessage(messageToSend);
  }, [query, isLoading, sendMessage]);

  // Gérer Enter pour envoyer (Shift+Enter pour nouvelle ligne)
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  }, [handleSubmit]);

  // Envoyer directement une suggestion
  const handleSuggestionClick = useCallback(async (suggestionText) => {
    if (isLoading) return;
    await sendMessage(suggestionText);
  }, [isLoading, sendMessage]);

  // Handle textarea change
  const handleTextareaChange = useCallback((e) => {
    setQuery(e.target.value);
  }, []);

  return (
    <div className={`llm-dashboard ${isWelcomeMode ? 'welcome-mode' : 'chat-mode'}`} ref={containerRef}>
      <div className="llm-container">
        {isWelcomeMode ? (
          // Écran de bienvenue - layout centré avec input intégré
          <div className="llm-welcome">
            <div className="llm-welcome-icon">
              <Icon name="message-circle" size={48} />
            </div>
            <h1 className="llm-welcome-title">
              {t('llm.welcome.title')}
            </h1>
            <p className="llm-welcome-subtitle">
              {t('llm.welcome.subtitle')}
            </p>
            
            {/* Input intégré dans le flow (pas fixed) - comme Perplexity */}
            <form className="llm-input-container llm-input-welcome" onSubmit={handleSubmit}>
              <div className="llm-input-wrapper">
                <textarea
                  ref={textareaRef}
                  className="llm-textarea"
                  placeholder={t('llm.inputPlaceholder')}
                  value={query}
                  onChange={handleTextareaChange}
                  onKeyDown={handleKeyDown}
                  disabled={isLoading}
                  rows={1}
                  autoComplete="off"
                  autoCorrect="on"
                />
                <button
                  type="submit"
                  className="llm-submit-button"
                  disabled={!query.trim() || isLoading}
                  aria-label={t('actions.submit')}
                >
                  <Icon name="send" size={20} />
                </button>
              </div>
              <p className="llm-input-hint">
                {t('llm.inputHint')}
              </p>
            </form>
            
            <div className="llm-suggestions">
              {suggestedQueries.map((suggestion, index) => (
                <button
                  key={index}
                  className="llm-suggestion-chip"
                  onClick={() => handleSuggestionClick(suggestion.text)}
                  disabled={isLoading}
                  aria-label={suggestion.text}
                >
                  <span className="llm-suggestion-icon" aria-hidden="true">
                    <Icon name={suggestion.icon} size={18} variant="outline" />
                  </span>
                  <span className="llm-suggestion-text">{suggestion.text}</span>
                </button>
              ))}
            </div>
            
            {/* CTA secondaire pour les utilisateurs non connectés */}
            {userState === 'anonymous' && (
              <div className="llm-auth-cta">
                <span className="llm-auth-cta-text">{t('llm.authCta.text', 'Connectez-vous pour des recommandations personnalisées')}</span>
              </div>
            )}
          </div>
        ) : (
          // Mode conversation
          <>
            <div className="llm-messages">
              {displayMessages.map((message) => (
                <div
                  key={message.id}
                  className={`llm-message llm-message-${message.role || message.type} ${message.error ? 'llm-message-error' : ''}`}
                >
                  <div className="llm-message-avatar">
                    {(message.role === 'user' || message.type === 'user') ? (
                      <Icon name="profile" size={24} />
                    ) : (
                      <Icon name="bot-assistant" size={24} />
                    )}
                  </div>
                  <div className="llm-message-content">
                    <p>{renderMessageContent(message.content)}</p>

                    {/* Chips de confirmation — CONFIRM_CREATION flow */}
                    {message.metadata?.blockReason === 'CONFIRM_CREATION' && message.uiResult?.collectedData && (
                      <div className="llm-confirm-chips">
                        <button
                          type="button"
                          className="llm-confirm-chip llm-confirm-chip-yes"
                          disabled={isLoading}
                          onClick={() => sendMessage('', {
                            uiAction: 'confirm_create_activity',
                            pendingActivity: message.uiResult.collectedData,
                          })}
                        >
                          ✅ {t('llm.confirmPublish')}
                        </button>
                        <button
                          type="button"
                          className="llm-confirm-chip llm-confirm-chip-cancel"
                          disabled={isLoading}
                          onClick={() => sendMessage(t('llm.modifyPrompt'))}
                        >
                          ✏️ {t('llm.modifyActivity')}
                        </button>
                      </div>
                    )}

                    {/* Afficher les action cards si présentes */}
                    {message.actionCards && message.actionCards.length > 0 && (() => {
                      // Pre-calculate values to avoid repeated computations
                      const hasRaskmoteActivities = message.actionCards.some(c => c.type === 'list' || c.type === 'confirm');
                      const firstLocalEventsIndex = message.actionCards.findIndex(c => c.type === 'local_events');
                      
                      return (
                      <>
                      <div className="llm-action-cards">
                        {message.actionCards.map((card, idx) => {
                          // RM-145: Rendre cliquables les cards list, confirm ET local_events (avec événements)
                          const isClickable = card.type === 'list' || 
                                              card.type === 'confirm' || 
                                              (card.type === 'local_events' && card.data?.events?.length > 0);
                          const activityId = card.data?.activityId || card.activityId;
                          
                          // Vérifier si on doit afficher le texte de transition avant cette card
                          const isFirstLocalEventsCard = card.type === 'local_events' && idx === firstLocalEventsIndex;
                          const shouldShowTransition = hasRaskmoteActivities && isFirstLocalEventsCard;
                          
                          const handleCardClick = () => {
                            if (card.type === 'list') {
                              if (process.env.NODE_ENV === 'development') {
                                console.log('[LlmDashboard] Opening feed modal');
                              }
                              pushModal({ type: 'feed' });
                            } else if (card.type === 'confirm' && activityId) {
                              if (process.env.NODE_ENV === 'development') {
                                console.log('[LlmDashboard] Opening activity modal for:', activityId);
                              }
                              pushModal({ type: 'activity', id: activityId });
                            } else if (card.type === 'local_events' && card.data?.events?.length > 0) {
                              // POC: Ouvrir la modal des événements locaux
                              if (process.env.NODE_ENV === 'development') {
                                console.log('[LlmDashboard] Opening local events modal', { count: card.data.events.length });
                              }
                              pushModal({ 
                                type: 'local_events', 
                                events: card.data.events,
                                sources: card.data.sources,
                                searchQuery: card.data.searchQuery,
                              });
                            }
                          };
                          
                          return (
                          <React.Fragment key={idx}>
                            {/* Texte de transition avant les événements externes */}
                            {shouldShowTransition && (
                              <div className="llm-events-transition">
                                <p>
                                  D'autres événements ont lieu autour de toi (sources externes) — {card.data?.summary?.count || card.data?.events?.length || 0} événements de type {card.data?.summary?.categories || 'varié'}, {card.data?.summary?.period || 'à venir'}.
                                </p>
                              </div>
                            )}
                            
                            <div 
                              className={`llm-action-card ${isClickable ? 'llm-action-card-clickable' : ''} ${card.type === 'local_events' ? 'llm-action-card-events' : ''}`}
                              onClick={isClickable ? handleCardClick : undefined}
                            role={isClickable ? 'button' : undefined}
                            tabIndex={isClickable ? 0 : undefined}
                            aria-label={isClickable ? (card.title || (card.type === 'confirm' ? t('llm.clickToViewActivity') : t('llm.createActivity'))) : undefined}
                            onKeyDown={(e) => {
                              if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                                e.preventDefault();
                                handleCardClick();
                              }
                            }}
                          >
                            {/* Icône pour les événements locaux */}
                            {card.type === 'local_events' && (
                              <span className="llm-action-card-icon">🗓️</span>
                            )}
                            <h4 className="llm-action-card-title">{card.title}</h4>
                            {card.description && (
                              <p className="llm-action-card-description">{card.description}</p>
                            )}
                            {card.date && (
                              <span className="llm-action-card-date">{card.date}</span>
                            )}
                            {/* Lien vers préférences si aucune activité trouvée */}
                            {card.data?.showPreferencesLink && (
                              <button
                                type="button"
                                className="llm-action-card-link"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  pushModal({ type: 'preferences' });
                                }}
                              >
                                {t('modifyPreferences', 'Modifier mes préférences')} →
                              </button>
                            )}
                            {/* Lien pour créer une activité via LLM */}
                            {card.data?.showCreateLink && (
                              <button
                                type="button"
                                className="llm-action-card-link llm-action-card-link-create"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Lancer directement la création via le LLM (prompt fixe en français)
                                  sendMessage('Je veux créer une activité');
                                }}
                              >
                                {t('llm.createActivity', 'Créer une activité')} →
                              </button>
                            )}
                            {/* RM-145: Hint pour les cards confirm cliquables */}
                            {card.type === 'confirm' && activityId && (
                              <div className="llm-action-card-hint">
                                👆 {t('llm.clickToViewActivity', 'Click to view activity')}
                              </div>
                            )}
                            {/* Hint pour les événements locaux */}
                            {card.type === 'local_events' && card.data?.events?.length > 0 && (
                              <div className="llm-action-card-hint llm-action-card-hint-events">
                                👆 {t('localEvents.clickToSee', 'Voir les événements')}
                              </div>
                            )}
                          </div>
                          </React.Fragment>
                        );
                        })}
                      </div>
                      
                      {/* Bouton opt-in pour recherche externe */}
                      {(() => {
                        const hasRaskmoteResults = message.actionCards.some(c => c.type === 'list' || c.type === 'confirm');
                        const hasExternalResults = message.actionCards.some(c => c.type === 'local_events');
                        const hasNoResults = message.actionCards.some(c => c.type === 'create_suggestion' && c.data?.showPreferencesLink);
                        // Ne PAS afficher le bouton après création d'activité
                        const hasActivityCreated = message.actionCards.some(c => c.type === 'confirm' && c.data?.activityId);
                        
                        // Vérifier si la feature est activée côté backend
                        const externalSearchEnabled = message.metadata?.features?.externalSearchEnabled ?? false;
                        
                        // Afficher le bouton si :
                        // - Feature activée
                        // - Résultats internes > 0 ET pas d'externes déjà affichés
                        // - OU 0 résultat
                        // - ET on n'a PAS créé d'activité
                        const shouldShowOptIn = externalSearchEnabled && (hasRaskmoteResults || hasNoResults) && !hasExternalResults && !hasActivityCreated;
                        
                        if (!shouldShowOptIn) return null;
                        
                        const buttonLabel = hasNoResults 
                          ? '🔍 Explorer les événements publics autour de moi' 
                          : '🌐 Approfondir avec des sources externes';
                        
                        return (
                          <button
                            className="llm-external-search-optin"
                            onClick={() => sendMessage('Oui, approfondis via des sources externes')}
                          >
                            {buttonLabel}
                          </button>
                        );
                      })()}
                      
                      {/* RM-149: CTA de progression externe (L1→L2→L3) - affiché SOUS la carte */}
                      {(() => {
                        // Chercher la carte local_events avec un CTA
                        const externalCard = message.actionCards?.find(c => c.type === 'local_events' && c.cta);
                        if (!externalCard?.cta || externalCard.cta.action !== 'UI_ACTION') {
                          return null;
                        }
                        
                        // Générer le message texte selon le niveau cible (avec mots-clés que le backend connaît)
                        const ctaMessages = {
                          progress_to_l2: 'Approfondir avec plus de sources publiques',
                          progress_to_l3: 'Voir plus de sources',
                        };
                        const messageText = ctaMessages[externalCard.cta.target] || 'Approfondir';
                        
                        return (
                          <button
                            className="llm-external-search-optin"
                            onClick={() => sendMessage(messageText)}
                          >
                            {externalCard.cta.label}
                          </button>
                        );
                      })()}
                      </>
                      );
                    })()}
                  </div>
                </div>
              ))}
              
              {/* Indicateur de chargement */}
              {isLoading && (
                <div className="llm-message llm-message-assistant">
                  <div className="llm-message-avatar">
                    <Icon name="bot-assistant" size={24} />
                  </div>
                  <div className="llm-message-content">
                    <div className="llm-loading">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Référence pour auto-scroll */}
              <div ref={messagesEndRef} />
            </div>

            {/* Input sticky en bas en mode conversation */}
            <form className="llm-input-container llm-input-sticky" onSubmit={handleSubmit}>
              <div className="llm-input-wrapper">
                <textarea
                  ref={textareaRef}
                  className="llm-textarea"
                  placeholder={t('llm.inputPlaceholder')}
                  value={query}
                  onChange={handleTextareaChange}
                  onKeyDown={handleKeyDown}
                  disabled={isLoading}
                  rows={1}
                  autoComplete="off"
                  autoCorrect="on"
                />
                <button
                  type="submit"
                  className="llm-submit-button"
                  disabled={!query.trim() || isLoading}
                  aria-label={t('actions.submit')}
                >
                  <Icon name="send" size={20} />
                </button>
              </div>
              <p className="llm-input-hint">
                {t('llm.inputHint')}
              </p>
            </form>
          </>
        )}
      </div>
      {/* Modal avec navigation stack */}
      <LlmModal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={
          currentModal?.type === 'feed' 
            ? t('menu.feed', 'Feed') 
            : currentModal?.type === 'activity' 
              ? t('activity.details', 'Détails de l\'activité')
              : currentModal?.type === 'preferences'
                ? t('settings.feedPreferences', 'Mes préférences')
                : currentModal?.type === 'local_events'
                  ? t('localEvents.title', 'Événements locaux')
                  : currentModal?.type === 'create'
                    ? t('llm.createActivity', 'Créer une activité')
                    : ''
        }
        showBackButton={modalStack.length > 1}
        onBack={popModal}
      >
        {/* Feed */}
        {currentModal?.type === 'feed' && (
          <HomePage 
            onActivityClick={(activityId) => {
              // Push l'activité sur la stack (garde le feed en dessous)
              pushModal({ type: 'activity', id: activityId });
            }}
          />
        )}
        
        {/* Activity Details */}
        {currentModal?.type === 'activity' && currentModal.id && (
          <ActivityDetailsPage 
            activityIdOverride={currentModal.id} 
            onBack={popModal}
          />
        )}
        
        {/* Preferences */}
        {currentModal?.type === 'preferences' && (
          <FeedPreferencesPage hideHeader />
        )}
        
        {/* POC: Local Events Modal */}
        {currentModal?.type === 'local_events' && (
          <LocalEventsModal
            events={currentModal.events || []}
            sources={currentModal.sources || []}
            searchQuery={currentModal.searchQuery || ''}
            onClose={closeModal}
            onBack={popModal}
            onCreateActivity={(presetData) => {
              // Ouvrir le formulaire de création avec les données pré-remplies
              if (process.env.NODE_ENV === 'development') {
                console.log('[LlmDashboard] Creating activity from event', presetData);
              }
              pushModal({ type: 'create', presetData });
            }}
          />
        )}
        
        {/* POC: Create Activity Form (pré-rempli depuis événement externe) */}
        {currentModal?.type === 'create' && (
          <CreateActivityAssistant
            onClose={closeModal}
            sourceEvent={currentModal.presetData ? {
              title: currentModal.presetData.title || currentModal.presetData.description || '',
              date: currentModal.presetData.date || null,
              location: currentModal.presetData.location || null,
              category: currentModal.presetData.category || null,
            } : null}
            onSuccess={() => {
              if (process.env.NODE_ENV === 'development') {
                console.log('[LlmDashboard] Activity created from external event');
              }
              closeModal();
            }}
          />
        )}
        
      </LlmModal>
    </div>
  );
};

export default LlmDashboard;
